import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { toast } from 'react-toastify';

import {
  INTERACTIVE_TOUR_START_EVENT,
  subscribeInteractiveTourProgress,
} from '../../tutorial/tourBridge';
import { tutorialSet } from '../../utils/tutorialStorage';

const KEY_INTERACTIVE_DONE = 'interactive_desktop_walkthrough_done';

function buildSteps(hasJobs) {
  return [
    {
      target: '[data-tour="nav-new-job"]',
      title: 'Create a job',
      content:
        'Click New Job in the sidebar (truck icon). The tour continues as soon as you open the form.',
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      hideFooter: true,
    },
    {
      target: '[data-tour="job-gcn-field"]',
      title: 'Add a test reference',
      content:
        'Enter any GCNNO you like (for example DEMO-001). This field is required before you can save a job.',
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
      hideFooter: true,
    },
    {
      target: '[data-tour="job-consignor-first"]',
      title: 'Shipper name',
      content:
        'Type a test first name for the consignor (e.g. Alex). Edit the default if needed — we detect a couple of characters you typed yourself.',
      placement: 'bottom',
      disableBeacon: true,
      spotlightClicks: true,
      hideFooter: true,
    },
    {
      target: '[data-tour="job-step-continue"]',
      title: 'Next section',
      content:
        'Click Continue to move to the locations step. You can return with Back anytime.',
      placement: 'top',
      disableBeacon: true,
      spotlightClicks: true,
      hideFooter: true,
    },
    {
      target: '[data-tour="nav-all-jobs"]',
      title: 'Browse jobs',
      content:
        'Open All Jobs in the sidebar to see saved moves. The tour continues when that page opens.',
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true,
      hideFooter: true,
    },
    hasJobs
      ? {
          target: '[data-tour="jobs-view-first"]',
          title: 'View a job',
          content:
            'Click the eye icon on a row to open read-only job details. That completes this walkthrough.',
          placement: 'left',
          disableBeacon: true,
          spotlightClicks: true,
          hideFooter: true,
        }
      : {
          target: '[data-tour="jobs-table-area"]',
          title: 'Your jobs will appear here',
          content:
            'You do not have any saved jobs in this list yet. After you fill the required fields and click Create job on the last step, the move shows up here. Click Finish when you are ready.',
          placement: 'top',
          disableBeacon: true,
          spotlightClicks: false,
          hideFooter: false,
        },
  ];
}

function markInteractiveTourDone() {
  tutorialSet(KEY_INTERACTIVE_DONE);
}

export default function InteractiveDesktopTour() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const { allJobs } = useSelector((state) => state.Job);

  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [joyKey, setJoyKey] = useState(0);
  const helpersRef = useRef(null);
  const runRef = useRef(false);
  const snapshotHasJobsRef = useRef(false);

  runRef.current = run;

  const endTour = useCallback(() => {
    setRun(false);
    setSteps([]);
    helpersRef.current = null;
    markInteractiveTourDone();
  }, []);

  const startTour = useCallback(() => {
    if (!isDesktop) {
      toast.info('The hands-on walkthrough is built for desktop — widen the window or use a larger screen.', {
        containerId: 'appLayoutToaster',
      });
      return;
    }
    const hasJobs = Array.isArray(allJobs) && allJobs.length > 0;
    snapshotHasJobsRef.current = hasJobs;
    setSteps(buildSteps(hasJobs));
    setJoyKey((k) => k + 1);
    setRun(true);
  }, [allJobs, isDesktop]);

  useEffect(() => {
    const onStart = () => startTour();
    window.addEventListener(INTERACTIVE_TOUR_START_EVENT, onStart);
    return () => window.removeEventListener(INTERACTIVE_TOUR_START_EVENT, onStart);
  }, [startTour]);

  useEffect(() => {
    return subscribeInteractiveTourProgress((ev) => {
      const h = helpersRef.current;
      if (!h || !runRef.current) return;
      let idx;
      try {
        idx = h.info().index;
      } catch {
        return;
      }
      if (idx === 1 && ev.type === 'gcn') {
        h.next();
      } else if (idx === 2 && ev.type === 'consignor') {
        h.next();
      } else if (idx === 3 && ev.type === 'form-step-1') {
        h.next();
      }
    });
  }, []);

  useEffect(() => {
    if (!run) return;
    const id = setInterval(() => {
      const h = helpersRef.current;
      if (!h || !runRef.current) return;
      let idx;
      try {
        idx = h.info().index;
      } catch {
        return;
      }
      const p = location.pathname;
      if (idx === 0 && p === '/create-job') {
        h.next();
      } else if (idx === 4 && p === '/jobs') {
        h.next();
      } else if (idx === 5 && snapshotHasJobsRef.current && p.includes('view-job')) {
        h.close();
        setRun(false);
        setSteps([]);
        markInteractiveTourDone();
      }
    }, 200);
    return () => clearInterval(id);
  }, [run, location.pathname]);

  const handleCallback = (data) => {
    const { status, action, type } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      endTour();
      return;
    }
    if (action === ACTIONS.CLOSE) {
      endTour();
      return;
    }
    if (type === EVENTS.TARGET_NOT_FOUND && runRef.current) {
      toast.warn('Tour target is not visible — expand the sidebar or scroll, then use Help to restart.', {
        containerId: 'appLayoutToaster',
      });
    }
  };

  if (!steps.length && !run) {
    return null;
  }

  return (
    <Joyride
      key={joyKey}
      run={run}
      steps={steps}
      continuous={false}
      showProgress={false}
      showSkipButton
      spotlightClicks
      disableOverlayClose
      scrollToFirstStep
      scrollOffset={120}
      callback={handleCallback}
      getHelpers={(helpers) => {
        helpersRef.current = helpers;
      }}
      locale={{ skip: 'Exit tour', last: 'Finish' }}
      styles={{
        options: {
          zIndex: 10050,
          primaryColor: theme.palette.primary.main,
          textColor: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          overlayColor: 'rgba(15, 23, 42, 0.72)',
          width: 380,
        },
      }}
    />
  );
}
